const trimTrailingSlash = (base: string) => base.replace(/\/$/, '');
const normalizeBase = (base?: string) => (base ?? '').trim();

const isLocalhost = (host: string) =>
  host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local');

export const resolveApiBase = (envBase?: string) => {
  const normalized = trimTrailingSlash(normalizeBase(envBase));

  if (typeof window === 'undefined') {
    return normalized;
  }

  const pageProtocol = window.location.protocol;
  const host = window.location.hostname;
  const isLocalhostHost = isLocalhost(host);

  try {
    const parsed = new URL(normalized, window.location.origin);
    const parsedPath = trimTrailingSlash(parsed.pathname);

    // Avoid mixed-content when page is HTTPS and env URL is HTTP:
    // - if it's the same host, degrade to same-origin relative path
    // - otherwise drop base entirely.
    if (pageProtocol === 'https:' && parsed.protocol === 'http:') {
      if (parsed.hostname === window.location.hostname) {
        return parsedPath || '';
      }
      return '';
    }

    // Drop localhost-only URLs when the page itself is not on localhost.
    if (!isLocalhostHost && isLocalhost(parsed.hostname)) {
      return '';
    }

    // For same-origin, keep path only.
    if (parsed.origin === window.location.origin) {
      return parsedPath || '';
    }

    return `${parsed.origin}${parsedPath}`;
  } catch {
    // Non-URL input (typically a path like /api/users)
    if (!isLocalhostHost && normalized.includes('localhost')) {
      return '';
    }
    return normalized;
  }
};

export const resolveApiBaseWithDefault = (envBase: string | undefined, defaultBase: string) => {
  const explicit = typeof envBase === 'string';
  const base = resolveApiBase(envBase);
  return explicit ? base : base || defaultBase;
};

export const buildApiUrl = (
  base: string,
  path: string,
  params?: Record<string, string | undefined>
) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const hasBasePrefix =
    base && (normalizedPath === base || normalizedPath.startsWith(`${base}/`));
  const basePath =
    base && normalizedPath === '/'
      ? base
      : base && !hasBasePrefix
        ? `${base}${normalizedPath}`
        : normalizedPath;

  if (!params) {
    return basePath;
  }

  const query = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]))
  ).toString();

  return query ? `${basePath}?${query}` : basePath;
};

export const resolveMesApiBase = (envBase?: string) => {
  const explicit = typeof envBase === 'string';
  const base = resolveApiBase(envBase);

  // Если явно передали пустую строку, позволяем использовать корень без префикса.
  if (explicit && base === '') {
    return '';
  }

  const defaultBase = '/mes';

  if (typeof window === 'undefined') {
    return base || (explicit ? '' : defaultBase);
  }

  if (!base) {
    return explicit ? '' : defaultBase;
  }

  try {
    const url = new URL(base, window.location.origin);
    const normalizedPath = trimTrailingSlash(url.pathname);

    if (url.origin === window.location.origin) {
      return normalizedPath || defaultBase;
    }

    return `${url.origin}${normalizedPath}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse MES API base', error);
    return defaultBase;
  }
};
